<script>
import { mapState } from 'vuex';
import isFunction from 'lodash/isFunction';
import flatPickr from 'vue-flatpickr-component';
import ReportModel from '@models/Report';
import DailyAllowanceModel from '@models/DailyAllowance';
import BlockingContainer from '@components/BlockingContainer';
import editFormChange from '@mixins/editFormChange';
import datePickerUtility from '@mixins/datePickerUtility';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { DateTime } from 'luxon';

export default {
  components: {
    BlockingContainer,
    flatPickr,
    FontAwesomeIcon,
  },
  mixins: [editFormChange, datePickerUtility],
  props: {
    // eslint-disable-next-line
    loadedId: {
      default: null,
    },
    // eslint-disable-next-line
    reportId: {
      required: true,
    },
  },
  data() {
    return {
      allowances: null,
      dailyAllowance: {
        meal_type: null,
        description: null,
        date: null,
        allowance_type: null,
        absence_start: null,
        absence_end: null,
        amount: null,
        allowance: null,
        allowance_id: null,
      },
      defaultDailyAllowance: {
        meal_type: null,
        description: null,
        date: null,
        absence_start: null,
        absence_end: null,
        allowance_type: null,
        amount: null,
        allowance: null,
        allowance_id: null,
      },
      timeConfig: {
        disableMobile: false,
        altInput: true,
        enableTime: true,
        noCalendar: true,
        altFormat: 'H:i',
        dateFormat: 'H:i',
        time_24hr: true,
        allowInput: true,
        minuteIncrement: 15,
      },
    };
  },
  computed: {
    ...mapState({
      dateConfig: (state) => state.common.dateOnlyConfig,
      allowanceTypes: (state) => state.common.allowanceTypes,
      meals: (state) => state.common.meals,
    }),
    totalAbsence() {
      if (this.dailyAllowance.absence_start !== null && this.dailyAllowance.absence_end !== null) {
        // fix negative values
        var tempAbsenceEnd = this.dailyAllowance.absence_end === '00:00' ? '24:00' : this.dailyAllowance.absence_end;
        const absenceStart = DateTime.fromFormat(this.dailyAllowance.absence_start, 'HH:mm');
        const absenceEnd = DateTime.fromFormat(tempAbsenceEnd, 'HH:mm');
        let diff = absenceEnd.diff(absenceStart, ['hours']).toObject();
        return diff.hours;
      } else {
        return null;
      }
    },
    allowanceType() {
      return this.dailyAllowance.allowance_type;
    },
    isFormValid() {
      return (
        this.dailyAllowance.allowance_type === this.getKeyByValue(this.allowanceTypes, this.allowanceTypes.overnight_stay) ||
        (this.dailyAllowance.allowance_type === this.getKeyByValue(this.allowanceTypes, this.allowanceTypes.meals) &&
          this.dailyAllowance.amount > 8 &&
          this.dailyAllowance.meal_type !== null) ||
        (this.dailyAllowance.allowance_type === this.getKeyByValue(this.allowanceTypes, this.allowanceTypes.ride_costs) && this.dailyAllowance.amount > 0)
      );
    },
    isUnsupportedMealSelected() {
      const unsupportedMealTypes = ['meals_minus_breakfast_and_lunch', 'meals_minus_breakfast_and_dinner', 'meals_minus_lunch_and_dinner'];
      return (this.dailyAllowance.allowance_type === this.getKeyByValue(this.allowanceTypes, this.allowanceTypes.meals) &&
        unsupportedMealTypes.includes(this.dailyAllowance.meal_type)) && this.dailyAllowance.amount < 24;
    }
  },
  watch: {
    allowanceType: {
      handler(val, oldVal) {
        // reset on change
        if (oldVal !== null && val !== oldVal) {
          this.dailyAllowance.amount = null;
        }

        if (val === this.getKeyByValue(this.allowanceTypes, this.allowanceTypes.overnight_stay)) {
          this.dailyAllowance.amount = 1;
        }
      },
    },
    totalAbsence: {
      handler(val) {
        this.dailyAllowance.amount = val;
      },
    },
  },
  mounted() {
    this.$wait.start('allowances loading');

    // fetch allowances

    this.$http
      .get('allowances', {
        params: {
          limit: 300,
          sort: 'country_order',
        },
      })
      .then((response) => {
        this.allowances = response.data.data;
        this.$wait.end('allowances loading');
      })
      .catch(() => this.$wait.end('allowances loading'));

    if (this.loadedId) {
      this.$wait.start('daily-allowance loading');
      new ReportModel({ id: this.reportId })
        .dailyAllowances()
        .find(this.loadedId)
        .then((response) => {
          this.dailyAllowance = Object.assign({}, response.daily_allowance);
          this.$wait.end('daily-allowance loading');
          this.$nextTick(() => {
            this.initChangeWatcher(['dailyAllowance'], this.validate);
          });
        });
    }
  },
  methods: {
    resetDailyAllowance() {
      this.dailyAllowance = Object.assign({}, this.defaultDailyAllowance);
      this.$validator.reset();
    },
    getKeyByValue(object, value) {
      return Object.keys(object).find((key) => object[key] === value);
    },
    validate(callback) {
      this.$validator.validateAll('daily-allowance-form').then((result) => {
        if (result) {
          if(this.isUnsupportedMealSelected) {
            this.$dialog.alert(this.$t('infos.unsupported_meals'), {
              html: true,
            }).then(() => this.resetDailyAllowance());
          } else {
            this.submit(callback);
          }
        }
      });
    },
    submit(callback) {
      const report = new ReportModel({ id: this.reportId });
      this.$wait.start('daily-allowance loading');

      new DailyAllowanceModel(Object.assign({}, this.dailyAllowance))
        .for(report)
        .save()
        .then((response) => {
          this.resetDailyAllowance();
          this.$emit('submitted', response.daily_allowance);
          if (callback && isFunction(callback)) callback();
          this.$wait.end('daily-allowance loading');
        })
        .catch(() => this.$wait.end('daily-allowance loading'));
    },
  },
};
</script>
<template>
  <div>
    <BlockingContainer v-if="$wait.is('daily-allowance loading') || $wait.is('allowances loading')">&nbsp;</BlockingContainer>
    <b-form data-vv-scope="daily-allowance-form" @submit.prevent="validate">
      <b-form-row>
        <b-col sm="4">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.date') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <div :class="{ 'is-invalid': errors.has('takeover_date', 'daily-allowance-form') }">
              <flat-pickr
                v-model="dailyAllowance.date"
                v-validate="'required'"
                data-vv-name="date"
                :data-vv-as="$t('fields.date')"
                aria-describedby="input-feedback-date"
                :config="dateConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('date', 'dailly-allowance-form') }"
                :placeholder="$t('placeholders.date')"
              >
              </flat-pickr>
              <b-form-invalid-feedback id="input-feedback-date">
                <template v-show="errors.has('date', 'daily-allowance-form')">{{ errors.first('date', 'daily-allowance-form') }} </template>
              </b-form-invalid-feedback>
            </div>
          </b-form-group>
        </b-col>
        <b-col sm="4">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12">
              {{ $t('fields.country') }}:
              <span class="status-star text-danger">*</span>
            </template>
            <b-form-select
              v-model="dailyAllowance.allowance_id"
              v-validate="'required'"
              data-vv-value-path="localValue"
              data-vv-name="allowance_id"
              :data-vv-as="$t('fields.country')"
              :state="errors.has('allowance_id', 'daily-allowance-form') ? false : null"
              aria-describedby="input-feedback-allowance_id"
            >
              <option :value="null">{{ $t('placeholders.select_country') }}</option>
              <option v-for="alw in allowances" :key="`countrySelect${alw.id}`" :value="alw.id">
                {{ alw.country.name_de }}
              </option>
            </b-form-select>
            <b-form-invalid-feedback id="input-feedback-allowance_id">
              <template v-show="errors.has('allowance_id', 'daily-allowance-form')">{{ errors.first('allowance_id', 'daily-allowance-form') }}</template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <template v-if="dailyAllowance.allowance_id != null && allowanceTypes != null">
          <b-col sm="4">
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.allowance_type') }}:
                <span class="status-star text-danger">*</span>
              </template>
              <div :class="{ 'is-invalid': errors.has('allowance_type', 'daily-allowance-form') }">
                <b-form-select
                  v-model="dailyAllowance.allowance_type"
                  v-validate="'required'"
                  data-vv-value-path="localValue"
                  data-vv-name="allowance_type"
                  :data-vv-as="$t('fields.allowance_type')"
                  :state="errors.has('allowance_type', 'daily-allowance-form') ? false : null"
                  aria-describedby="input-feedback-allowance_type"
                >
                  <option :value="null">{{ $t('placeholders.allowance_type') }}</option>
                  <option v-for="(allT, key) in allowanceTypes" :key="`allowanceType${key}`" :value="key">
                    {{ allT }}
                  </option>
                </b-form-select>
                <b-form-invalid-feedback id="input-feedback-allowance_type">
                  <template v-show="errors.has('allowance_type', 'daily-allowance-form')"
                    >{{ errors.first('allowance_type', 'daily-allowance-form') }}
                  </template>
                </b-form-invalid-feedback>
              </div>
            </b-form-group>
          </b-col>
        </template>
      </b-form-row>
      <b-form-row v-if="dailyAllowance.allowance_type != null">
        <template v-if="dailyAllowance.allowance_type == getKeyByValue(allowanceTypes, allowanceTypes.meals)">
          <b-col sm="4">
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.absence_start') }}:
                <span class="status-star text-danger">*</span>
              </template>
              <flat-pickr
                v-model="dailyAllowance.absence_start"
                v-validate="'required'"
                data-vv-name="date"
                :data-vv-as="$t('fields.date')"
                aria-describedby="input-feedback-date"
                :config="timeConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('date', 'dailly-allowance-form') }"
                :placeholder="$t('placeholders.time')"
                @on-close="closeCalendar('dailyAllowance.absence_start', null, null, ...arguments)"
              >
              </flat-pickr>
            </b-form-group>
          </b-col>
          <b-col sm="4">
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.absence_end') }}:
                <span class="status-star text-danger">*</span>
              </template>
              <flat-pickr
                v-model="dailyAllowance.absence_end"
                v-validate="'required'"
                data-vv-name="date"
                :data-vv-as="$t('fields.date')"
                aria-describedby="input-feedback-date"
                :config="timeConfig"
                class="form-control"
                :class="{ 'is-invalid': errors.has('date', 'dailly-allowance-form') }"
                :placeholder="$t('placeholders.time')"
                @on-close="closeCalendar('dailyAllowance.absence_end', null, null, ...arguments)"
              >
              </flat-pickr>
            </b-form-group>
          </b-col>
          <b-col sm="4">
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.total_absence') }}: </template>
              <span v-if="totalAbsence === null" class="text-danger">
                {{ $t('messages.please_enter_absence_times') }}
              </span>
              <b-form-input v-else disabled :value="totalAbsence" :state="totalAbsence > 8"></b-form-input>
              <b-form-text v-if="totalAbsence != null && totalAbsence <= 8" text-variant="danger">
                {{ $t('errors.period_not_legitimate_for_allowance') }}</b-form-text
              >
            </b-form-group>
          </b-col>
          <b-col v-if="totalAbsence > 8" sm="8">
            <b-form-group>
              <template v-slot:label class="col-form-legend col-12">
                {{ $t('fields.meals') }}:
                <span class="status-star text-danger">*</span>
                <span class="near-label-info text-secondary" @click.stop>
                  <FontAwesomeIcon id="mealPopover" :icon="['fas', 'info-circle']" tabindex="-1" />
                  <b-popover target="mealPopover" triggers="focus" placement="rightbottom">
                    {{ $t('infos.meals') }}
                  </b-popover>
                </span>
              </template>
              <div :class="{ 'is-invalid': errors.has('meal_type', 'daily-allowance-form') }">
                <b-form-select
                  v-model="dailyAllowance.meal_type"
                  v-validate="'required'"
                  data-vv-value-path="localValue"
                  data-vv-name="meal_type"
                  :data-vv-as="$t('fields.meals')"
                  :state="errors.has('meal_type', 'daily-allowance-form') ? false : null"
                  aria-describedby="input-feedback-meal_type"
                >
                  <option :value="null">{{ $t('placeholders.meal_type') }}</option>
                  <option v-for="(meal, key) in meals" :key="`allowanceType${key}`" :value="key">
                    {{ meal }}
                  </option>
                </b-form-select>
                <b-form-invalid-feedback id="input-feedback-meal_type">
                  <template v-show="errors.has('meal_type', 'daily-allowance-form')">{{ errors.first('meal_type', 'daily-allowance-form') }} </template>
                </b-form-invalid-feedback>
              </div>
            </b-form-group>
          </b-col>
        </template>
        <b-col v-else-if="dailyAllowance.allowance_type == getKeyByValue(allowanceTypes, allowanceTypes.ride_costs)">
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.kilometers') }}: </template>
            <b-form-input
              v-model="dailyAllowance.amount"
              v-validate="'required'"
              type="number"
              step="0.1"
              data-vv-value-path="localValue"
              data-vv-name="amount"
              :data-vv-as="$t('fields.kilometers')"
              :state="errors.has('amount', 'daily-allowance-form') ? false : null"
              aria-describedby="input-feedback-amount"
              :placeholder="$t('placeholders.kilometers')"
            >
            </b-form-input>
            <b-form-invalid-feedback id="input-feedback-amount">
              <template v-show="errors.has('amount', 'daily-allowance-form')">{{ errors.first('amount', 'daily-allowance-form') }} </template>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row v-if="isFormValid">
        <b-col>
          <b-form-group>
            <template v-slot:label class="col-form-legend col-12"> {{ $t('fields.description') }}: </template>
            <b-form-textarea
              id="description"
              v-model="dailyAllowance.description"
              v-validate="'max:65000'"
              data-vv-value-path="localValue"
              data-vv-name="description"
              :rows="3"
              :state="errors.has('description', 'daily-allowance-form') ? false : null"
              aria-describedby="input-feedback-description"
            >
            </b-form-textarea>
          </b-form-group>
        </b-col>
      </b-form-row>
      <template>
        <b-form-row> </b-form-row>
        <b-button
          ref="dailyAllowanceSubmitBtn"
          type="submit"
          :disabled="errors.any('daily-allowance-form') || !isFormValid"
          :variant="'outline-' + (!errors.any('daily-allowance-form') ? 'dark' : 'danger')"
        >
          {{ $t('actions.submit') }}
        </b-button>
      </template>
    </b-form>
  </div>
</template>
<style>
.dg-pull-right {
  float: inherit !important;
}

.dg-content-footer {
  text-align: center;
}
</style>
